import apiClient from './api';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface BrandProfile {
  id: string;
  organization_id: string;
  brand_name?: string;
  industry?: string;
  sub_industry?: string;
  logo_url?: string;
  logo_dark_url?: string;
  colors: Record<string, any>;
  fonts: Record<string, any>;
  voice_keywords?: string[];
  tagline?: string;
  business_description?: string;
  website_url?: string;
  extracted_data?: Record<string, any>;
  setup_completed: boolean;
  created_at: string;
  updated_at: string;
}

export interface BrandProfileRequest {
  brand_name?: string;
  industry?: string;
  sub_industry?: string;
  logo_url?: string | null;
  logo_dark_url?: string | null;
  colors?: Record<string, any>;
  fonts?: Record<string, any>;
  voice_keywords?: string[];
  tagline?: string;
  business_description?: string;
  website_url?: string;
  setup_completed?: boolean;
}

export interface ColorExtractionResponse {
  dominant_color: number[];
  palette: number[][];
  hex_colors: string[];
}

export interface WebsiteExtractionResponse {
  business_description: string;
  highlights: string[];
  services: string[];
  target_audience: string;
  tone: string[];
  taglines: string[];
  proof_points: string[];
  suggested_colors: string[];
  og_image: string;
  source_urls: string[];
  warning: string;
  // Passed through to brand knowledge (pre-fills the strategy questions).
  business_name?: string;
  category?: string;
  locations?: string[];
  pricing?: string;
  entry_offer?: string;
  phone?: string;
}

export interface TemplateSlot {
  id: string;
  type: 'text' | 'image' | 'images' | 'video';
  label: string;
  maxLength?: number;
  placeholder?: string;
}

export interface VibeTemplateListItem {
  id: string;
  name: string;
  slug: string;
  description?: string;
  type: 'static' | 'video';
  format?: string;
  width: number;
  height: number;
  duration_frames?: number;
  fps: number;
  thumbnail_url?: string;
  preview_gif_url?: string;
  product_image_url?: string;
  tags: string[];
  industry: string[];
  occasion: string[];
  mood: string[];
  supported_languages: string[];
  design_family_id?: string;
  slot_count: number;
  has_image_slot: boolean;
  has_text_slot: boolean;
  usage_count: number;
  engine?: 'legacy' | 'generic' | 'code';
}

export interface VibeTemplateDetail extends VibeTemplateListItem {
  template_json?: {
    tracks: any[];
    durationFrames?: number;
    fps?: number;
  };
  layout_spec?: {
    canvas: { width: number; height: number; fps: number; durationFrames: number; background?: string };
    designBrief?: any;
    root: any;
  };
  /** engine === 'code' only: raw JSX source (app/creative_engine/codegen.py). */
  generated_code?: string;
  /** engine === 'code' only: esbuild bundle ready for LivePreviewFrame — bundled server-side
   * on detail fetch so the browser can render a live preview without a second round-trip. */
  bundle_js?: string;
  /** engine === 'code' only: Stage 1 output (bigIdea/mood/palette/copy/...) — the same
   * shape the generic engine nests under layout_spec.designBrief, stored as its own
   * field here since code-engine templates have no layout_spec tree. */
  design_brief?: DesignBrief;
  slots: TemplateSlot[];
  version: number;
  family_formats: string[];
  /** Created by the current org (offers "Same ad, another size" on Ad Studio creatives). */
  is_own?: boolean;
}

// ─── Code-generation engine (staged compose -> approve -> animate flow) ────────────

export interface DesignBriefPalette {
  background: string;
  surface: string;
  text: string;
  textMuted: string;
  accent: string;
  cta: string;
  ctaText: string;
}

export interface DesignBriefCopyElement {
  id: string;
  text: string;
  role: string;
  emphasis: 'primary' | 'loud' | 'normal' | 'muted' | string;
}

export interface DesignBriefProductPlacement {
  position: string;
  frameShare: string;
  surfaceOrContact: string;
  /** "none" if nobody touches/wears/uses the product; otherwise a description. Purely
   * descriptive — not read by any backend logic. */
  interaction: string;
  copySpace: string;
}

export interface DesignBrief {
  bigIdea: string;
  productCategory: string;
  targetAudience: string;
  mood: string[];
  colorDirection: string;
  palette: DesignBriefPalette;
  layoutArchetype: string;
  photographyTreatment: string;
  motionConcept?: string;
  /** The staging decision (position/pose/interaction/copy space) Stage 1 made — kept
   * consistent with imagePrompt/scenePrompt. Absent on briefs generated before this
   * field existed. */
  productPlacement?: DesignBriefProductPlacement;
  /** Standalone text-to-image prompt for the hero/product photo, derived by Stage 1 —
   * see vibeApi.generateCodeImage. Absent on briefs generated before this field existed. */
  imagePrompt?: string;
  rationale: string;
  /** The language the copy was written in, named in English ("Hindi", "Hinglish (English
   * letters)") — reported by Stage 1. Absent on briefs generated before this field existed. */
  copyLanguage?: string;
  copy: DesignBriefCopyElement[];
  /** Persisted deterministic legibility plan (recolor/scrim per text) computed at save time,
   * so reopening a saved code template re-applies the exact fix the approved preview used and
   * the color no longer drifts across preview/library/export. Absent on older templates. */
  _legibility?: Array<Record<string, any>>;
  [key: string]: any;
}

export interface EngineConfigResponse {
  engine: 'legacy' | 'generic' | 'code';
}

/** Shared context fields across every staged code-engine request — mirrors the backend's
 * CodeStageContextSchema (services/media-generation-service/app/api/vibe_routes.py). */
export interface CodeStageContext {
  type: 'static' | 'video';
  format: '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1';
  industry?: string;
  occasion?: string;
  mood?: string;
  language?: string;
  context?: string;
  brand_colors?: Record<string, any>;
  brand_fonts?: Record<string, any>;
  use_brand_tokens?: boolean;
  /** Whether to include the org's brand logo (if one is set) in this generation.
   * Independent of use_brand_tokens — a user may want brand colors without the logo, or
   * vice versa. Defaults to true server-side if omitted. */
  use_logo?: boolean;
  model?: string;
  /** Where this creative's COMPOSITION comes from. 'auto' (default): the AI decides.
   * 'template': the user picked one from listCodeStyles — style_id names it. 'reference':
   * the user attached an ad they like — style_ref carries what extractCodeStyle read from
   * it. Mutually exclusive; the server reads style_id/style_ref through this field, so a
   * stale value left in the other one is ignored rather than silently applied. */
  style_source?: StyleSource;
  style_id?: string;
  style_ref?: StyleRef;
}

export type StyleSource = 'auto' | 'template' | 'reference';

/** Composition, typography, and palette read from a user's reference image. Deliberately
 * carries nothing about that reference's product or copy. Treated as an opaque
 * pass-through by the frontend — extracted by the server, echoed back on brief/compose —
 * except for `summary`, which is rendered as chips so the user can see what was taken. */
export type StyleRef = Record<string, any>;

export interface CodeStyle {
  id: string;
  title: string;
  blurb: string;
  /** 'cutout' (needs an isolated product shot), 'scene' (needs a composed photograph), or
   * null (works either way). */
  requires_image: 'cutout' | 'scene' | null;
  requires_feature_points: boolean;
  /** False when this composition can't be built from the product photo the caller
   * declared. Entries are never omitted — disable with the reason rather than reshuffling
   * the gallery as the user flips the image toggle. */
  buildable: boolean;
  /** data: URI of a pre-rendered PNG, or null if it hasn't been rendered. Rendered from
   * PLACEHOLDER brand/product data: it shows structure only, never the user's output. */
  thumbnail: string | null;
}

export interface CodeStylesResponse {
  success: boolean;
  styles: CodeStyle[];
}

export interface CodeExtractStyleResponse {
  success: boolean;
  /** Null when nothing usable could be read — tell the user the reference couldn't be
   * used rather than generating as though it had been applied. */
  style_ref?: StyleRef | null;
  summary: string[];
  /** The image actually read — for a pasted web-page link, the page's preview image.
   * Use it as the reference thumbnail. */
  image_url?: string | null;
  error?: string;
}

export interface CodeBriefRequest extends CodeStageContext {
  prompt: string;
  reference_images?: string[];
  /** Only meaningful when reference_images is set. 'as_is': the first reference image will
   * be used unmodified as the final hero photo — the brief is written to match it exactly
   * instead of assuming an image will be generated. 'regenerate' or omitted: today's default
   * — the brief directs a new scene via imagePrompt/scenePrompt as normal. */
  product_photo_intent?: 'as_is' | 'regenerate';
  /** Only meaningful when NO reference image is attached. 'typography': compose a text-led
   * creative with no hero image (imagePrompt left empty). 'generate' or omitted: the brief
   * authors a hero image as normal. */
  image_mode?: 'generate' | 'typography';
  /** True when reference_images are DIFFERENT products to be shown together in one hero
   * image (Ad Studio "Different products"), not angles of one product. */
  product_range?: boolean;
  /** With product_range: one name per reference image, in order (e.g. flavours). */
  product_labels?: string[];
}

/** Free follow-up attempts a paid brief (or resize) includes. */
export interface GenerationAllowances {
  compose: number;
  animate: number;
}

export interface CodeBriefResponse {
  success: boolean;
  design_brief?: DesignBrief;
  /** Proof this brief was paid for — compose and animate must send it back. */
  generation_token?: string;
  allowances?: GenerationAllowances;
  error?: string;
}

export interface CodeComposeRequest extends CodeStageContext {
  design_brief: DesignBrief;
  /** generation_token from the paid brief this composition belongs to. */
  generation_token?: string;
  /** Resolved product photo URL (generated via generateCodeImage or picked from the media
   * library) — passed to Stage 2 as vision input. Optional; omit for no product photo. */
  product_image_url?: string;
  /** True when product_image_url came from generateCodeImage (a full scene already
   * composed by the image-generation stage) rather than picked raw from the media
   * library. Tells Stage 2 whether to render it full-bleed or treat it as a cutout. */
  product_image_is_composed_scene?: boolean;
  /** Transparent-background product PNG (from generateCodeImage's plate mode). When
   * absent and the photo is raw, the backend silently attempts a direct key and echoes
   * the result back in CodeBundleResponse.product_cutout_url. */
  product_cutout_url?: string;
}

/** "Same ad, another size": compose for `format` (the new size) from the approved still. */
/** "Same ad, another size" of a saved creative (the server loads its brief, photo and code). */
export interface CodeResizeRequest {
  source_template_id: string;
  format: '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1';
}

/** The new size's still composition plus what's needed to animate (video) and save it. */
export interface CodeResizeResponse extends CodeBundleResponse {
  design_brief?: DesignBrief;
  type?: 'static' | 'video';
  /** A paid resize opens its own session — send this to animate the new size. */
  generation_token?: string;
  allowances?: GenerationAllowances;
  product_image_url?: string;
  use_logo?: boolean;
  use_brand_tokens?: boolean;
  industry?: string;
  occasion?: string;
  language?: string;
}

export interface CodeAnimateRequest extends CodeStageContext {
  design_brief: DesignBrief;
  generated_code: string;
  /** generation_token from the paid video brief (or resize) being animated. */
  generation_token?: string;
  product_image_url?: string;
}

export interface CodeBundleResponse {
  success: boolean;
  generated_code?: string;
  bundle_js?: string;
  /** The cutout this composition was generated against (caller-provided or derived
   * server-side during compose) — carry it to animate previews, save, and renders. */
  product_cutout_url?: string;
  error?: string;
}

export interface CodeGenerateImageRequest {
  design_brief: DesignBrief;
  /** Set for a Regenerate of an existing product image (not a first generation) — the
   * backend switches to image_to_image, anchored to this asset, so the product itself is
   * preserved rather than redrawn from scratch. */
  source_asset_id?: string;
  /** URL of the user's product photo (internet URL or media library URL) to anchor
   * image-to-image generation on first generation. Ignored when source_asset_id is set.
   * The backend imports it into the media library (deduplicating on repeat) and uses it
   * as the source image so the generated ad preserves the actual product. */
  source_image_url?: string;
  /** Product range: the source product's name (e.g. its flavour). */
  source_label?: string;
  /** Product range: up to 4 more DIFFERENT products composed into ONE scene with the
   * source product. The result is label-checked and regenerated once if a label is wrong. */
  additional_products?: Array<{ asset_id?: string; url?: string; label?: string }>;
}

/** Product range only: whether every product appears with correctly spelled labels. */
export interface ProductLabelCheck {
  checked: boolean;
  ok: boolean | null;
  issues: string[];
  attempts?: number;
}

export interface CodeGenerateImageResponse {
  success: boolean;
  url?: string;
  asset_id?: string;
  width?: number;
  height?: number;
  /** Set when the brief chose a layered archetype and the backend's flat-plate keying
   * succeeded: a transparent product PNG to pass to compose as product_cutout_url. */
  cutout_url?: string;
  /** True when the image was generated as a flat-color plate for keying rather than a
   * lifestyle scene. */
  is_plate?: boolean;
  label_check?: ProductLabelCheck;
  error?: string;
}

export interface CodeSaveRequest {
  design_brief: DesignBrief;
  generated_code: string;
  type: 'static' | 'video';
  format: '1:1' | '4:5' | '2:3' | '9:16' | '16:9' | '1.91:1';
  industry?: string;
  occasion?: string;
  language?: string;
  /** The resolved product photo this creative was composed with (same URL passed to
   * compose), so the saved row gets a real thumbnail and reopens with the right photo. */
  product_image_url?: string;
  /** The transparent product cutout the composition was generated against (echoed by
   * compose), persisted so thumbnails/exports rebind the 'product-cutout' slot. */
  product_cutout_url?: string;
  /** Ad Studio "use logo" / "use brand colours" choices — saved with the creative so the
   * thumbnail, editor and exports honor them. */
  use_logo?: boolean;
  use_brand_tokens?: boolean;
  /** Id of the saved creative this is another size of — joins its design family. */
  family_of?: string;
}

export interface CodeSaveResponse {
  success: boolean;
  template?: {
    id?: string;
    name: string;
    slug: string;
    description: string;
    type: string;
    format: string;
    width: number;
    height: number;
    duration_frames?: number;
    fps?: number;
    engine: string;
    generated_code?: string;
    design_brief?: DesignBrief;
    design_family_id: string;
    thumbnail_url?: string;
  };
  error?: string;
}

export interface VibeTemplateListResponse {
  templates: VibeTemplateListItem[];
  total: number;
  page: number;
  page_size: number;
  has_more: boolean;
}

export interface TemplateFilterOptions {
  types: string[];
  formats: string[];
  industries: string[];
  occasions: string[];
  moods: string[];
  languages: string[];
}

// ─── Brand Kit Types ──────────────────────────────────────────────────────

export interface BrandKitAsset {
  id: string;
  organization_id: string;
  asset_type: string;
  variant?: string;
  file_url: string;
  thumbnail_url?: string;
  file_name: string;
  original_file_name?: string;
  file_size?: number;
  mime_type?: string;
  metadata: Record<string, any>;
  sort_order: number;
  uploaded_by_user_id?: string;
  created_at?: string;
  updated_at?: string;
}

export interface BrandKitListResponse {
  assets: BrandKitAsset[];
  total: number;
}

export type BrandKitAssetType =
  | 'logo' | 'logo_dark' | 'icon' | 'favicon' | 'watermark'
  | 'brand_font' | 'brand_guideline' | 'color_palette'
  | 'signature' | 'pattern' | 'texture' | 'sound_logo';

export interface TemplateRenderRequest {
  user_data: Record<string, any>;
  output_format: 'png' | 'jpg' | 'mp4' | 'gif';
  brand_profile_id?: string;
  track_overrides?: Record<number, Record<string, any>>;
  /** 'generated' keeps the AI-chosen palette as-is; 'brand' re-skins to the org's brand colors. */
  color_mode?: 'generated' | 'brand';
  /** Per-creative logo control. When false, the brand logo is blanked before render. */
  show_logo?: boolean;
  /** Optional per-creative logo replacement URL (applies only when show_logo is true). */
  logo_url?: string;
}

export interface TemplateRenderResponse {
  success: boolean;
  job_id?: string;
  status?: string;
  estimated_time_seconds?: number;
  error?: string;
}

export interface TemplateFilters {
  type?: string;
  format?: string;
  industry?: string;
  occasion?: string;
  mood?: string;
  language?: string;
  search?: string;
  tag?: string;
  design_family_id?: string;
  custom_only?: boolean;
  page?: number;
  page_size?: number;
}

// ─── API Methods ────────────────────────────────────────────────────────────

const VIBE_BASE = '/api/v1';
const scriptFontCache = new Map<string, Promise<string>>();

export const vibeApi = {
  // Brand Profile
  getBrandProfile: async (): Promise<BrandProfile | null> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/brand-profile`);
    return data;
  },

  saveBrandProfile: async (profile: BrandProfileRequest): Promise<BrandProfile> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/brand-profile`, profile);
    return data;
  },

  extractColors: async (fileOrUrl: File | string): Promise<ColorExtractionResponse> => {
    const formData = new FormData();
    if (typeof fileOrUrl === 'string') {
      formData.append('logo_url', fileOrUrl);
    } else {
      formData.append('file', fileOrUrl);
    }
    const { data } = await apiClient.post(`${VIBE_BASE}/brand-profile/extract-colors`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },

  /** Fetch a company website and extract structured business details (renders JS
   *  SPAs server-side + runs the cheap model). Returns an editable draft — the
   *  caller reviews it, then persists business_description via saveBrandProfile. */
  extractWebsite: async (url: string): Promise<WebsiteExtractionResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/brand-profile/extract-website`, { url });
    return data;
  },

  checkBrandProfileComplete: async (): Promise<{
    complete: boolean;
    missing: string[];
    setup_completed: boolean;
  }> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/brand-profile/complete`);
    return data;
  },

  // Templates
  listTemplates: async (filters: TemplateFilters = {}): Promise<VibeTemplateListResponse> => {
    const params: Record<string, any> = {};
    if (filters.type) params.type = filters.type;
    if (filters.format) params.format = filters.format;
    if (filters.industry) params.industry = filters.industry;
    if (filters.occasion) params.occasion = filters.occasion;
    if (filters.mood) params.mood = filters.mood;
    if (filters.language) params.language = filters.language;
    if (filters.search) params.search = filters.search;
    if (filters.tag) params.tag = filters.tag;
    if (filters.design_family_id) params.design_family_id = filters.design_family_id;
    if (filters.custom_only) params.custom_only = true;
    params.page = filters.page || 1;
    params.page_size = filters.page_size || 24;

    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates`, { params });
    return data;
  },

  getTemplate: async (templateId: string): Promise<VibeTemplateDetail> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/${templateId}`);
    return data;
  },

  getFilterOptions: async (): Promise<TemplateFilterOptions> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/filters`);
    return data;
  },

  getDesignFamily: async (familyId: string): Promise<VibeTemplateListItem[]> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/family/${familyId}`);
    return data;
  },

  // Code-generation engine (staged compose -> approve -> animate flow)
  getEngineConfig: async (): Promise<EngineConfigResponse> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/engine-config`);
    return data;
  },

  /** Static @fontsource CSS, identical across every generation — fetch once and reuse
   * for every LivePreviewFrame instance rather than re-fetching per stage. */
  getCodePreviewCss: async (): Promise<string> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/code/preview-css`);
    return data.css;
  },

  /** Font CSS for non-Latin scripts (ids from components/vibe-kit/scripts.ts), layered on
   * getCodePreviewCss by LivePreviewFrame when an ad's copy uses them. Cached per script for
   * the session — each is a few hundred KB and never changes. */
  getScriptFontCss: (scripts: string[]): Promise<string> =>
    Promise.all(scripts.map(id => {
      let p = scriptFontCache.get(id);
      if (!p) {
        p = apiClient.get(`${VIBE_BASE}/vibe-templates/code/script-fonts`, { params: { scripts: id } })
          .then(({ data }) => data.css as string)
          .catch(() => { scriptFontCache.delete(id); return ''; });
        scriptFontCache.set(id, p);
      }
      return p;
    })).then(parts => parts.filter(Boolean).join('\n')),

  /** The style gallery — every composition a user can build on, with a pre-rendered
   * thumbnail. Static and identical for every org, so fetch once and keep it.
   * `imageKind` only sets each entry's `buildable` flag; nothing is filtered server-side. */
  listCodeStyles: async (imageKind?: 'cutout' | 'scene'): Promise<CodeStylesResponse> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/code/styles`, {
      params: imageKind ? { image_kind: imageKind } : undefined,
    });
    return data;
  },

  /** Read an attached reference ad's composition into a style_ref. Free, and run at
   * ATTACH time so its latency hides while the user is still writing their brief — cache
   * the result against the image so regenerating reuses identical style input. */
  extractCodeStyle: async (imageUrl: string): Promise<CodeExtractStyleResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/extract-style`, {
      image_url: imageUrl,
    });
    return data;
  },

  generateCodeBrief: async (request: CodeBriefRequest): Promise<CodeBriefResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/brief`, request);
    return data;
  },

  /** Optional stage between brief and compose: generate the hero/product photo from the
   * approved brief (prompt derived server-side, never hand-typed here). Skippable — the
   * caller may instead pass an uploaded photo's URL straight to generateCodeComposition. */
  generateCodeImage: async (request: CodeGenerateImageRequest): Promise<CodeGenerateImageResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/generate-image`, request);
    return data;
  },

  generateCodeComposition: async (request: CodeComposeRequest): Promise<CodeBundleResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/compose`, request);
    return data;
  },

  /** "Same ad, another size" — charged like a fresh creative; no new brief or image. */
  resizeCodeComposition: async (request: CodeResizeRequest): Promise<CodeResizeResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/resize`, request);
    return data;
  },

  animateCodeComposition: async (request: CodeAnimateRequest): Promise<CodeBundleResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/animate`, request);
    return data;
  },

  /** Re-bundle already-generated/stored source for preview (e.g. opening a saved
   * code-engine template) — no LLM call. Pass the template's persisted legibility plan
   * (design_brief._legibility) so the reopened preview re-applies the exact recolor/scrim
   * the approved compose preview used, instead of reverting to the raw code's colors. */
  bundleCodeTemplate: async (
    generatedCode: string,
    legibility?: Array<Record<string, any>> | null,
  ): Promise<CodeBundleResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/bundle`, {
      generated_code: generatedCode,
      ...(legibility && legibility.length ? { legibility } : {}),
    });
    return data;
  },

  deleteTemplate: async (templateId: string): Promise<void> => {
    await apiClient.delete(`${VIBE_BASE}/vibe-templates/${templateId}`);
  },

  saveCodeTemplate: async (request: CodeSaveRequest): Promise<CodeSaveResponse> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/code/save`, request);
    return data;
  },

  // Render
  renderTemplate: async (
    templateId: string,
    request: TemplateRenderRequest
  ): Promise<TemplateRenderResponse> => {
    const { data } = await apiClient.post(
      `${VIBE_BASE}/vibe-templates/${templateId}/render`,
      request
    );
    return data;
  },

  // Template Review
  submitForReview: async (template: {
    name: string;
    description?: string;
    type: string;
    format: string;
    width: number;
    height: number;
    duration_frames?: number;
    fps?: number;
    template_json: any;
    tags?: string[];
    industry?: string[];
    occasion?: string[];
    mood?: string[];
    supported_languages?: string[];
    design_family_id?: string;
  }): Promise<{ success: boolean; id: string; status: string }> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/submit-for-review`, template);
    return data;
  },

  listPendingReview: async (page = 1, pageSize = 24): Promise<{
    templates: any[];
    total: number;
  }> => {
    const { data } = await apiClient.get(`${VIBE_BASE}/vibe-templates/pending-review`, {
      params: { page, page_size: pageSize },
    });
    return data;
  },

  approveTemplate: async (templateId: string, notes?: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/${templateId}/approve`, {
      notes,
    });
    return data;
  },

  rejectTemplate: async (templateId: string, notes?: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post(`${VIBE_BASE}/vibe-templates/${templateId}/reject`, {
      notes,
    });
    return data;
  },

  // Brand Kit
  listBrandKitAssets: async (assetType?: string): Promise<BrandKitListResponse> => {
    const params: Record<string, any> = {};
    if (assetType) params.asset_type = assetType;
    const { data } = await apiClient.get(`${VIBE_BASE}/brand-kit`, { params });
    return data;
  },

  uploadBrandKitAsset: async (
    file: File,
    assetType: BrandKitAssetType,
    variant?: string,
  ): Promise<BrandKitAsset> => {
    const formData = new FormData();
    formData.append('file', file);
    const params: Record<string, string> = { asset_type: assetType };
    if (variant) params.variant = variant;
    const { data } = await apiClient.post(`${VIBE_BASE}/brand-kit/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params,
    });
    return data;
  },

  updateBrandKitAsset: async (
    assetId: string,
    updates: { variant?: string; sort_order?: number; metadata?: Record<string, any> },
  ): Promise<BrandKitAsset> => {
    const { data } = await apiClient.patch(`${VIBE_BASE}/brand-kit/${assetId}`, updates);
    return data;
  },

  deleteBrandKitAsset: async (assetId: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.delete(`${VIBE_BASE}/brand-kit/${assetId}`);
    return data;
  },

  deleteAllBrandKitAssets: async (assetType?: string): Promise<{ success: boolean; deleted: number }> => {
    const params = assetType ? { asset_type: assetType } : {};
    const { data } = await apiClient.delete(`${VIBE_BASE}/brand-kit`, { params });
    return data;
  },
};

export default vibeApi;
